import { Maybe } from '@aspendental/themes';
import { ThemeOptions } from '@mui/material/styles';
import { TypographyProps } from '@mui/material/Typography';

import { LOVET_TYPOGRAPHY_VARIANTS } from './lovetTypographyVariants';

import { TAGBrand } from '@shared-components/types';

export interface TypographyVariant {
	name: string;
	style: {
		fontFamily: string;
		fontStyle: string;
		fontWeight: number;
		fontSize: string;
		lineHeight: string;
		letterSpacing?: string | number;
	};
}

const DEFAULT_TYPOGRAPHY_VARIANTS: TypographyVariant[] = [
	{
		name: 'heroLarge',
		style: {
			fontFamily: 'Tiempos Headline',
			fontStyle: 'normal',
			fontWeight: 400,
			fontSize: 'clamp(3.56rem, 5.13vi + -0.02rem, 6.13rem)',
			lineHeight: 'clamp(4.25rem, 6.25vi + -0.13rem, 7.38rem)',
			letterSpacing: '-0.5px',
		},
	},
	{
		name: 'heroMedium',
		style: {
			fontFamily: 'Tiempos Headline',
			fontStyle: 'normal',
			fontWeight: 400,
			fontSize: 'clamp(2.81rem, 4vi + 0.01rem, 4.81rem)',
			lineHeight: 'clamp(3.38rem, 4.75vi + 0.05rem, 5.75rem)',
			letterSpacing: '-0.03125rem',
		},
	},
	{
		name: 'header1',
		style: {
			fontFamily: 'Tiempos Headline',
			fontStyle: 'normal',
			fontWeight: 400,
			fontSize: 'clamp(2.25rem, 3.13vi + 0.06rem, 3.81rem)',
			lineHeight: 'clamp(2.69rem, 3.75vi + 0.06rem, 4.56rem)',
			letterSpacing: '-0.5px',
		},
	},
	{
		name: 'header2',
		style: {
			fontFamily: 'Tiempos Headline',
			fontStyle: 'normal',
			fontWeight: 400,
			fontSize: 'clamp(1.75rem, 2.5vi, 3rem)',
			lineHeight: 'clamp(2.13rem, 3vi + 0.02rem, 3.63rem)',
			letterSpacing: 0,
		},
	},
	{
		name: 'header3',
		style: {
			fontFamily: 'Messina Sans',
			fontStyle: 'normal',
			fontWeight: 600,
			fontSize: 'clamp(1.38rem, 1.88vi + 0.06rem, 2.31rem)',
			lineHeight: 'clamp(1.63rem, 2.25vi + 0.05rem, 2.75rem)',
			letterSpacing: '-0.5px',
		},
	},
	{
		name: 'bodyMediumBook',
		style: {
			fontFamily: 'Messina Sans',
			fontStyle: 'normal',
			fontWeight: 350,
			fontSize: 'clamp(0.75rem, 1.13vi + -0.04rem, 1.31rem)',
			lineHeight: 'clamp(1.25rem, 1.38vi + 0.29rem, 1.94rem)',
			letterSpacing: 0,
		},
	},
	{
		name: 'bodyMediumBold',
		style: {
			fontFamily: 'Messina Sans',
			fontStyle: 'normal',
			fontWeight: 700,
			fontSize: 'clamp(0.75rem, 1.13vi + -0.04rem, 1.31rem)',
			lineHeight: 'clamp(1.25rem, 1.38vi + 0.29rem, 1.94rem)',
			letterSpacing: 0,
		},
	},
	{
		name: 'bodyLargeBook',
		style: {
			fontFamily: 'Messina Sans',
			fontStyle: 'normal',
			fontWeight: 300,
			fontSize: 'clamp(0.88rem, 1.25vi, 1.5rem)',
			lineHeight: 'clamp(1.31rem, 1.88vi, 2.25rem)',
			letterSpacing: 0,
		},
	},
	{
		name: 'bodyLargeBold',
		style: {
			fontFamily: 'Messina Sans',
			fontStyle: 'normal',
			fontWeight: 700,
			fontSize: 'clamp(0.88rem, 1.25vi, 1.5rem)',
			lineHeight: 'clamp(1.31rem, 1.88vi, 2.25rem)',
			letterSpacing: 0,
		},
	},
];

export function getTypographyVariants(branding?: Maybe<TAGBrand>): TypographyVariant[] {
	if (branding === TAGBrand.Lovet) {
		return LOVET_TYPOGRAPHY_VARIANTS;
	}
	return DEFAULT_TYPOGRAPHY_VARIANTS;
}

export const applyFluidTypography = (
	brandTheme: Partial<ThemeOptions>,
	branding?: Maybe<TAGBrand>
): Partial<ThemeOptions> => {
	if (!brandTheme.components?.MuiTypography?.variants) {
		return brandTheme;
	}

	const updatedVariants = [...brandTheme.components.MuiTypography.variants];

	getTypographyVariants(branding).forEach(({ name, style }) => {
		// @ts-ignore - MUI variant type compatibility
		const variantIndex = updatedVariants.findIndex((variant: any) => variant.props?.variant === name);

		if (variantIndex !== -1) {
			updatedVariants[variantIndex] = {
				props: { variant: name as TypographyProps['variant'] },
				style: () => style,
			};
		}
	});

	return {
		...brandTheme,
		components: {
			...brandTheme.components,
			MuiTypography: {
				...brandTheme.components.MuiTypography,
				variants: updatedVariants,
			},
		},
	};
};
